import React, { memo } from 'react';
import { Trans } from 'react-i18next';

import Icon from '@common/components/icon';
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@common/components/ui/dropdown-menu';

import type { CustomUserPropertyPredicateFilter } from '../../utils/build-custom-user-property-predicate-filters';
import { getCustomUserPropertyTypeIcon } from '../../utils/get-type-icon';

type UserPropertiesPredicateSubmenuProps = {
  filters: CustomUserPropertyPredicateFilter[];
  existingFields: string[];
  onFilterSelect: (attribute: string) => boolean;
  onAddFilter: (filter: CustomUserPropertyPredicateFilter) => void;
};

function isFilterDisabled(
  filter: CustomUserPropertyPredicateFilter,
  existingFields: string[]
): boolean {
  return existingFields.includes(filter.value);
}

export const UserPropertiesPredicateSubmenu = memo(({
  filters,
  existingFields,
  onFilterSelect,
  onAddFilter,
}: UserPropertiesPredicateSubmenuProps) => {
  if (filters.length === 0) return null;

  const handleSelect = (filter: CustomUserPropertyPredicateFilter) => {
    if (isFilterDisabled(filter, existingFields)) return;
    if (!onFilterSelect(filter.value)) return;
    onAddFilter(filter);
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="tw-cursor-pointer">
        <Icon type="integration" className="tw-mr-2" />
        <Trans i18nKey="common:predicates_filter_predicate_user_properties" />
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        className="tw-z-[1200] tw-max-h-[min(400px,70vh)] tw-min-w-[12rem] tw-overflow-y-auto"
        collisionPadding={12}
      >
        {filters.map((filter) => (
          <DropdownMenuItem
            key={filter.value}
            disabled={isFilterDisabled(filter, existingFields)}
            onSelect={() => handleSelect(filter)}
          >
            <Icon type={getCustomUserPropertyTypeIcon(filter.propertyType)} />
            {filter.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
});

UserPropertiesPredicateSubmenu.displayName = 'UserPropertiesPredicateSubmenu';
